import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Phone, Mail, MapPin, Share2, ArrowLeft, Globe, Facebook, Instagram, Twitter, MessageCircle, ChevronDown, UserPlus, X, Grid, ChevronLeft, ChevronRight } from 'lucide-react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { getImageUrl, buildResponsiveImage } from '../lib/imageUtils';
import { trackLinkClick } from '../lib/analytics';
import { getStallShareUrl } from '../lib/slug';

const StallModal = ({ stall, isOpen, onClose, filterMarket, onGetDirections }) => {
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [showMarketDropdown, setShowMarketDropdown] = useState(false);
  const [bannerError, setBannerError] = useState(false);
  const [logoError, setLogoError] = useState(false);

  // Defensive onError: if a srcset variant 404s, strip the srcset and retry the
  // main src. Only flag as fully errored once the main URL has also failed.
  const handleImgError = (setError) => (e) => {
    const img = e.currentTarget;
    if (img.getAttribute('srcset')) {
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      const original = img.src;
      img.src = '';
      img.src = original;
      return;
    }
    setError(true);
  };
  const [showAllPhotos, setShowAllPhotos] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [singlePhotoView, setSinglePhotoView] = useState(false);
  const [vendorFullName, setVendorFullName] = useState(null);

  // Ref callback to attach native touch/wheel listeners on the lightbox container.
  // react-remove-scroll blocks touch events from elements outside the Dialog Content
  // by calling preventDefault on document-level listeners. We stop propagation at the
  // lightbox level so those listeners never see the events.
  const lightboxRef = useCallback((node) => {
    if (!node) return;
    const stop = (e) => e.stopPropagation();
    node.addEventListener('touchmove', stop, { passive: true });
    node.addEventListener('touchstart', stop, { passive: true });
    node.addEventListener('wheel', stop, { passive: true });
  }, []);
  
  // Get available markets from locations array
  const availableMarkets = stall?.locations?.filter(loc => loc.market && (loc.lat || loc.lng)) || [];
  
  // Initialize selected market and reset image states when stall changes
  useEffect(() => {
    if (stall) {
      // Reset image error states
      setBannerError(false);
      setLogoError(false);

      // If a market filter is active and this stall has that market, use it
      if (filterMarket && filterMarket !== 'All Markets' && availableMarkets.length > 0) {
        const matchingMarket = availableMarkets.find(loc => loc.market === filterMarket);
        if (matchingMarket) {
          setSelectedMarket(matchingMarket.market);
          return;
        }
      }

      // Prefer a location whose type matches the stall's root type (or the first
      // known type). This ensures expo/festival stalls don't default to a legacy
      // market location that happens to sit first in the array.
      const rootType = stall.type || (stall.locations?.[0]?.type) || null;
      const typed = availableMarkets.find(l => l.type && l.type === rootType);

      if (typed) {
        setSelectedMarket(typed.market);
      } else if (availableMarkets.length > 0) {
        // Prefer a location matching stall.market by name (root primary), then fall back to first
        const byName = availableMarkets.find(l => l.market === stall.market);
        setSelectedMarket((byName || availableMarkets[0]).market);
      } else if (stall.market) {
        setSelectedMarket(stall.market);
      }
    }
  }, [stall?._id, filterMarket]);

  // Fetch the linked vendor's full name for display in Contact section
  useEffect(() => {
    if (!stall?._id) { setVendorFullName(null); return; }
    let cancelled = false;
    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/stalls/${stall._id}/vendor-name`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled) setVendorFullName(d?.fullName || null); })
      .catch(() => { if (!cancelled) setVendorFullName(null); });
    return () => { cancelled = true; };
  }, [stall?._id]);

  if (!stall) return null;

  // Get image URLs
  const banner = stall.banner ? buildResponsiveImage(stall.banner, { fallbackWidth: 1920 }) : { src: null, srcSet: null };
  const logoResp = stall.logo ? buildResponsiveImage(stall.logo, { fallbackWidth: 800 }) : { src: null, srcSet: null };
  const bannerUrl = banner.src;
  const logoUrl = logoResp.src;

  // Get location for selected market
  const getSelectedLocation = () => {
    if (availableMarkets.length > 0 && selectedMarket) {
      const marketLoc = availableMarkets.find(loc => loc.market === selectedMarket);
      if (marketLoc) {
        return { lat: marketLoc.lat, lng: marketLoc.lng, stallNumber: marketLoc.stallNumber };
      }
    }
    // Fallback to legacy single location
    return { 
      lat: stall.location?.lat, 
      lng: stall.location?.lng, 
      stallNumber: stall.stallNumber 
    };
  };
  
  const currentLocation = getSelectedLocation();

  const handleGetDirections = () => {
    trackLinkClick('directions', 'directions', stall._id, stall.stallName, selectedMarket || stall.market);
    
    const { lat, lng } = currentLocation;
    if (lat && lng) {
      onClose(); // Close the modal
      if (onGetDirections) {
        onGetDirections(stall, lat, lng, currentLocation.market || selectedMarket || stall.market);
      }
    } else {
      alert('Location coordinates not available for this market');
    }
  };

  const handleContactClick = (linkType, url) => {
    trackLinkClick(linkType, 'contact', stall._id, stall.stallName);
    window.open(url, linkType === 'website' ? '_blank' : '_self');
  };

  const handleSocialClick = (platform, url) => {
    trackLinkClick(platform, 'social_media', stall._id, stall.stallName);
    window.open(url, '_blank');
  };

  const handleShare = async () => {
    const shareUrl = getStallShareUrl(stall);
    const shareMessage = `Check out "${stall.stallName}" on WAYO — the vendor platform.`;
    // Track the share intent — analytics fires regardless of whether the native
    // share sheet completes or falls back to clipboard.
    trackLinkClick('share', 'engagement', stall._id, stall.stallName);

    if (navigator.share) {
      try {
        await navigator.share({
          title: `${stall.stallName} | WAYO`,
          text: shareMessage,
          url: shareUrl,
        });
      } catch (err) {
        // User cancelled or share failed, copy URL to clipboard
        if (err.name !== 'AbortError') {
          navigator.clipboard.writeText(shareUrl);
          alert('Link copied to clipboard!');
        }
      }
    } else {
      // Copy just the URL - the OG tags will provide the preview text
      navigator.clipboard.writeText(shareUrl);
      alert('Link copied to clipboard!');
    }
  };

  const handleMarketSelect = (market) => {
    setSelectedMarket(market);
    setShowMarketDropdown(false);
  };

  const handleAddToContacts = () => {
    const stallId = stall._id || stall.id;
    trackLinkClick('save_contact', 'engagement', stallId, stall.stallName);
    const vcardUrl = `${window.location.origin}/api/stalls/${stallId}/vcard`;
    const link = document.createElement('a');
    link.href = vcardUrl;
    link.setAttribute('download', '');
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
    <DialogPrimitive.Root open={isOpen} onOpenChange={(open) => { if (!open) { setShowAllPhotos(false); setPhotoIndex(0); setSinglePhotoView(false); onClose(); } }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className="fixed left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] z-50 w-[95vw] max-w-3xl bg-white dark:bg-[#1a1a1a] shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 rounded-2xl max-h-[90vh] overflow-hidden border-4 border-[#FFD12D] flex flex-col"
          onInteractOutside={(e) => { if (showAllPhotos) e.preventDefault(); }}
          onPointerDownOutside={(e) => { if (showAllPhotos) e.preventDefault(); }}
        >
          
          {/* Sticky Back Button - Fixed position within modal */}
          <Button
            onClick={onClose}
            variant="ghost"
            className="absolute top-3 left-3 sm:top-4 sm:left-4 z-[60] w-10 h-10 sm:w-12 sm:h-12 p-0 rounded-full bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D] shadow-lg transition-all duration-300 transform hover:scale-110"
          >
            <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6" />
          </Button>
        
          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden">
            <div className="relative h-48 sm:h-64 overflow-hidden bg-gray-200 dark:bg-gray-800">
              {/* Banner Image */}
              {bannerUrl && !bannerError ? (
                <img 
                  src={bannerUrl}
                  srcSet={banner.srcSet || undefined}
                  sizes="(max-width: 768px) 100vw, 768px"
                  alt={stall.stallName}
                  className="w-full h-full object-cover"
                  loading="eager"
                  onError={handleImgError(setBannerError)}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-700 dark:to-gray-800">
                  <span className="text-gray-500 dark:text-gray-400">
                    {bannerError ? 'Banner unavailable' : 'No Banner'}
                  </span>
                </div>
              )}
              
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
              <div className="absolute bottom-4 left-4 sm:bottom-6 sm:left-6 flex items-end gap-3 sm:gap-4">
                <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-xl sm:rounded-2xl overflow-hidden border-3 sm:border-4 border-white shadow-2xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                  {/* Logo Image */}
                  {logoUrl && !logoError ? (
                    <img 
                      src={logoUrl}
                      srcSet={logoResp.srcSet || undefined}
                      sizes="96px"
                      alt={`${stall.stallName} logo`}
                      className="w-full h-full object-cover"
                      loading="eager"
                      onError={handleImgError(setLogoError)}
                    />
                  ) : (
                    <span className="text-gray-400 text-sm">{logoError ? '!' : 'Logo'}</span>
                  )}
                </div>
                <div className="text-white pb-1 sm:pb-2 max-w-[calc(100%-5rem)] sm:max-w-none">
                  <h2 className="text-lg sm:text-3xl font-bold mb-0.5 sm:mb-1 break-words line-clamp-2">{stall.stallName}</h2>
                  <p className="text-[#FFD12D] text-xs sm:text-lg font-semibold italic break-words">{stall.slogan}</p>
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-8 space-y-4 sm:space-y-6 overflow-x-hidden">
            {/* Market Selector and Location Info */}
            <div className="flex items-center gap-3 text-gray-700 dark:text-gray-300">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#FFD12D]/20 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-[#FFD12D]" />
              </div>
              <div className="flex-1">
                {/* Market info with location */}
                <p className="font-bold text-[#252525] dark:text-white">
                  {selectedMarket || stall.market}{selectedMarket || stall.market ? ', ' : ''}Stall: {currentLocation.stallNumber || stall.stallNumber}
                </p>
                {/* Other locations dropdown - right below market heading */}
                {availableMarkets.length > 1 && (
                  <div className="relative mt-1">
                    <button
                      onClick={() => setShowMarketDropdown(!showMarketDropdown)}
                      className="text-sm sm:text-base text-gray-600 dark:text-gray-300 hover:text-[#FFD12D] dark:hover:text-[#FFD12D] transition-colors flex items-center gap-1 font-medium"
                    >
                      Other locations: {availableMarkets.filter(loc => loc.market !== selectedMarket).map(loc => loc.market).join(', ')}
                      <ChevronDown className={`w-4 h-4 transition-transform ${showMarketDropdown ? 'rotate-180' : ''}`} />
                    </button>
                    {showMarketDropdown && (
                      <div className="absolute top-full left-0 mt-2 bg-white dark:bg-[#252525] border-2 border-[#FFD12D] rounded-lg shadow-xl z-10 min-w-[200px]">
                        {availableMarkets.map((loc) => (
                          <button
                            key={loc.market}
                            onClick={() => handleMarketSelect(loc.market)}
                            className={`w-full text-left px-4 py-3 hover:bg-[#FFD12D]/20 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                              selectedMarket === loc.market ? 'bg-[#FFD12D]/30 font-bold' : ''
                            }`}
                          >
                            <div className="font-semibold text-[#252525] dark:text-white">{loc.market}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">Stall: {loc.stallNumber || 'N/A'}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {stall.category && (
                  <p className="text-sm font-semibold text-[#FFD12D]">Category: {stall.category}</p>
                )}
                {stall.tags && stall.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {stall.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-[#FFD12D]/20 text-[#252525] dark:text-[#FFD12D] text-sm font-semibold rounded-full border border-[#FFD12D]"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <Separator className="bg-gray-200 dark:bg-gray-700" />

            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-[#252525] dark:text-white mb-2 sm:mb-3 flex items-center gap-2">
                About Us
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-sm sm:text-base">{stall.about}</p>
            </div>

            {/* Gallery Section */}
            {stall.gallery && stall.gallery.length > 0 && (
              <>
                <Separator className="bg-gray-200 dark:bg-gray-700" />
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-[#252525] dark:text-white mb-3 flex items-center gap-2">
                    Gallery
                  </h3>
                  {/* Airbnb-style grid: 1 large + up to 4 small — same layout on all screens */}
                  <div className="rounded-xl overflow-hidden">
                    {stall.gallery.length === 1 ? (
                      <div
                        className="aspect-[16/9] cursor-pointer"
                        onClick={() => setShowAllPhotos(true)}
                      >
                        <img
                          src={getImageUrl(stall.gallery[0])}
                          alt="Gallery 1"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : stall.gallery.length === 2 ? (
                      <div className="grid grid-cols-2 gap-1 aspect-[16/9]">
                        {stall.gallery.slice(0, 2).map((img, i) => (
                          <div key={i} className="cursor-pointer overflow-hidden" onClick={() => setShowAllPhotos(true)}>
                            <img src={getImageUrl(img)} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover" />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="grid grid-cols-4 gap-1" style={{ aspectRatio: '16/9' }}>
                        {/* Large image - takes left half */}
                        <div
                          className="col-span-2 row-span-2 cursor-pointer overflow-hidden"
                          onClick={() => setShowAllPhotos(true)}
                        >
                          <img
                            src={getImageUrl(stall.gallery[0])}
                            alt="Gallery 1"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        {/* Up to 4 smaller images - right half */}
                        {stall.gallery.slice(1, 5).map((img, i) => (
                          <div
                            key={i}
                            className="relative cursor-pointer overflow-hidden"
                            onClick={() => setShowAllPhotos(true)}
                          >
                            <img
                              src={getImageUrl(img)}
                              alt={`Gallery ${i + 2}`}
                              className="w-full h-full object-cover"
                            />
                            {/* "Show all" overlay on last visible small image */}
                            {i === 3 && stall.gallery.length > 5 && (
                              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                <span className="text-white font-semibold text-sm">+{stall.gallery.length - 5} more</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {stall.gallery.length > 1 && (
                    <button
                      onClick={() => setShowAllPhotos(true)}
                      className="mt-3 flex items-center gap-2 px-4 py-2 rounded-full border-2 border-[#252525] dark:border-white text-[#252525] dark:text-white font-semibold text-sm hover:bg-[#252525] hover:text-white dark:hover:bg-white dark:hover:text-[#252525] transition-colors"
                      data-testid="show-all-photos-btn"
                    >
                      <Grid className="w-4 h-4" />
                      Show all {stall.gallery.length} photos
                    </button>
                  )}
                </div>

                {/* Gallery lightbox trigger is handled outside Dialog */}
              </>
            )}

            <Separator className="bg-gray-200 dark:bg-gray-700" />

            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-[#252525] dark:text-white mb-1">Vendor Contact Information</h3>
              {vendorFullName && (
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-3 sm:mb-4" data-testid="stall-holder-name">
                  Stall Holder Name: <span className="font-semibold text-[#252525] dark:text-white">{vendorFullName}</span>
                </p>
              )}
              {!vendorFullName && <div className="mb-3 sm:mb-4" />}
              <div className="space-y-2 sm:space-y-3">
                {stall.phone && (
                  <button 
                    onClick={() => handleContactClick('phone', `tel:${stall.phone}`)}
                    className="w-full flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-full bg-[#FFD12D]/10 border-2 border-[#FFD12D] hover:bg-[#FFD12D]/20 transition-all duration-300 group text-left"
                  >
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#FFD12D] flex items-center justify-center transition-all duration-300 flex-shrink-0">
                      <Phone className="w-5 h-5 sm:w-6 sm:h-6 text-[#252525]" />
                    </div>
                    <p className="text-gray-900 dark:text-white font-bold text-base sm:text-lg">Phone Vendor</p>
                  </button>
                )}

                {stall.whatsapp && (
                  <button 
                    onClick={() => handleContactClick('whatsapp', `https://wa.me/${stall.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(`Hi, I found you on Wayo and I am interested in`)}`)}
                    className="w-full flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-full bg-[#FFD12D]/10 border-2 border-[#FFD12D] hover:bg-[#FFD12D]/20 transition-all duration-300 group text-left"
                  >
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#FFD12D] flex items-center justify-center transition-all duration-300 flex-shrink-0">
                      <svg className="w-6 h-6 text-[#252525]" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                      </svg>
                    </div>
                    <p className="text-gray-900 dark:text-white font-bold text-base sm:text-lg">Whatsapp Vendor</p>
                  </button>
                )}

                {stall.email && (
                  <button 
                    onClick={() => handleContactClick('email', `mailto:${stall.email}`)}
                    className="w-full flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-full bg-[#FFD12D]/10 border-2 border-[#FFD12D] hover:bg-[#FFD12D]/20 transition-all duration-300 group text-left"
                  >
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#FFD12D] flex items-center justify-center transition-all duration-300 flex-shrink-0">
                      <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-[#252525]" />
                    </div>
                    <p className="text-gray-900 dark:text-white font-bold text-base sm:text-lg">Email Vendor</p>
                  </button>
                )}

                {stall.website && (
                  <button 
                    onClick={() => handleContactClick('website', stall.website)}
                    className="w-full flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-full bg-[#FFD12D]/10 border-2 border-[#FFD12D] hover:bg-[#FFD12D]/20 transition-all duration-300 group text-left"
                  >
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#FFD12D] flex items-center justify-center transition-all duration-300 flex-shrink-0">
                      <Globe className="w-5 h-5 sm:w-6 sm:h-6 text-[#252525]" />
                    </div>
                    <p className="text-gray-900 dark:text-white font-bold text-base sm:text-lg">Visit Vendor Website</p>
                  </button>
                )}
              </div>
            </div>

            {/* Only show Social Media section if at least one social link exists */}
            {(stall.socialMedia?.facebook || stall.socialMedia?.instagram || 
              stall.socialMedia?.tiktok || stall.socialMedia?.twitter || stall.socialMedia?.linkedin) && (
              <>
                <Separator className="bg-gray-200 dark:bg-gray-700" />

                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-[#252525] dark:text-white mb-3 sm:mb-4">Social Media</h3>
                  <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
                    {stall.socialMedia?.facebook && (
                      <button
                        onClick={() => handleSocialClick('facebook', stall.socialMedia.facebook)}
                        className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-[#FFD12D]/20 hover:bg-[#FFD12D] flex items-center justify-center transition-all duration-300 transform hover:scale-110"
                        title="Facebook"
                      >
                        <Facebook className="w-7 h-7 text-[#252525]" />
                      </button>
                    )}
                    {stall.socialMedia?.instagram && (
                      <button
                        onClick={() => handleSocialClick('instagram', stall.socialMedia.instagram)}
                        className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-[#FFD12D]/20 hover:bg-[#FFD12D] flex items-center justify-center transition-all duration-300 transform hover:scale-110"
                        title="Instagram"
                      >
                        <Instagram className="w-7 h-7 text-[#252525]" />
                      </button>
                    )}
                    {stall.socialMedia?.tiktok && (
                      <button
                        onClick={() => handleSocialClick('tiktok', stall.socialMedia.tiktok)}
                        className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-[#FFD12D]/20 hover:bg-[#FFD12D] flex items-center justify-center transition-all duration-300 transform hover:scale-110"
                        title="TikTok"
                      >
                        <svg className="w-7 h-7 text-[#252525]" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                        </svg>
                      </button>
                    )}
                    {stall.socialMedia?.twitter && (
                      <button
                        onClick={() => handleSocialClick('twitter', stall.socialMedia.twitter)}
                        className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-[#FFD12D]/20 hover:bg-[#FFD12D] flex items-center justify-center transition-all duration-300 transform hover:scale-110"
                        title="X (Twitter)"
                      >
                        <svg className="w-6 h-6 text-[#252525]" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                        </svg>
                      </button>
                    )}
                    {stall.socialMedia?.linkedin && (
                      <button
                        onClick={() => handleSocialClick('linkedin', stall.socialMedia.linkedin)}
                        className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-[#FFD12D]/20 hover:bg-[#FFD12D] flex items-center justify-center transition-all duration-300 transform hover:scale-110"
                        title="LinkedIn"
                      >
                        <svg className="w-7 h-7 text-[#252525]" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Get Directions and Share buttons */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-4 pb-2">
              <Button
                onClick={handleGetDirections}
                disabled={!currentLocation.lat || !currentLocation.lng}
                size="lg"
                className="flex-1 min-h-[60px] py-4 bg-[#FFD12D] hover:bg-[#252525] text-[#252525] hover:text-[#FFD12D] font-bold text-sm sm:text-base transition-all duration-300 shadow-md disabled:opacity-50 disabled:cursor-not-allowed rounded-full"
              >
                <MapPin className="w-5 h-5 mr-2" />
                {`Directions to ${selectedMarket || stall.market || 'Market'}`}
              </Button>
              <div className="flex gap-2 sm:gap-3">
                <Button
                  onClick={handleAddToContacts}
                  size="lg"
                  className="flex-1 sm:flex-none min-h-[60px] py-4 px-6 bg-[#252525] hover:bg-[#FFD12D] text-white hover:text-[#252525] transition-all duration-300 shadow-md rounded-full"
                >
                  <UserPlus className="w-5 h-5 mr-2 sm:mr-0" />
                  <span className="sm:hidden">Add Contact</span>
                </Button>
                <Button
                  onClick={handleShare}
                  size="lg"
                  className="flex-1 sm:flex-none min-h-[60px] py-4 px-6 border-2 border-[#FFD12D] bg-transparent text-[#252525] dark:text-white hover:bg-[#FFD12D] hover:text-[#252525] transition-all duration-300 shadow-md rounded-full"
                >
                  <Share2 className="w-5 h-5 mr-2 sm:mr-0" />
                  <span className="sm:hidden">Share</span>
                </Button>
              </div>
            </div>
            
            {/* Show hint if no location available */}
            {!currentLocation.lat && !currentLocation.lng && (
              <p className="text-sm text-center text-gray-500 dark:text-gray-400">
                Location not available for this stall
              </p>
            )}
          </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>

    {/* Gallery lightbox — portaled to document.body to escape Radix Dialog stacking context */}
    {showAllPhotos && stall?.gallery && createPortal(
      <div
        ref={lightboxRef}
        className="fixed inset-0 bg-black"
        style={{ zIndex: 99999, pointerEvents: 'auto' }}
        data-testid="gallery-lightbox"
        onClick={(e) => e.stopPropagation()}
      >
        {/* === MOBILE: Scrollable vertical gallery (< 768px) === */}
        <div className="md:hidden flex flex-col h-full">
          {/* Sticky header */}
          <div className="sticky top-0 flex items-center justify-between px-4 py-3 bg-black/90 backdrop-blur-sm" style={{ zIndex: 100000 }}>
            <span className="text-white font-semibold text-sm">{stall.gallery.length} photos</span>
            <button
              onClick={() => { setShowAllPhotos(false); setPhotoIndex(0); setSinglePhotoView(false); }}
              className="bg-[#FFD12D] hover:bg-[#e6bc28] text-[#252525] rounded-full w-10 h-10 flex items-center justify-center transition-colors"
              data-testid="close-gallery-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          {/* Scrollable photos */}
          <div
            className="flex-1 overflow-y-auto px-3 pb-6 space-y-3 overscroll-contain"
            style={{ WebkitOverflowScrolling: 'touch' }}
            data-testid="gallery-mobile-scroll"
          >
            {stall.gallery.map((img, i) => (
              <div key={i} className="w-full rounded-lg overflow-hidden">
                <img
                  src={getImageUrl(img)}
                  alt={`Gallery ${i + 1}`}
                  className="w-full h-auto object-contain bg-gray-900 rounded-lg"
                />
              </div>
            ))}
          </div>
        </div>

        {/* === DESKTOP/TABLET: Grid overview → Click to view single photo (>= 768px) === */}
        <div className="hidden md:flex flex-col h-full">
          {!singlePhotoView ? (
            <>
              {/* Grid overview header */}
              <div className="flex items-center justify-between px-6 py-4" style={{ zIndex: 100000 }}>
                <span className="text-white font-semibold text-base">{stall.gallery.length} photos</span>
                <button
                  onClick={() => { setShowAllPhotos(false); setPhotoIndex(0); setSinglePhotoView(false); }}
                  className="bg-[#FFD12D] hover:bg-[#e6bc28] text-[#252525] rounded-full w-10 h-10 flex items-center justify-center transition-colors"
                  data-testid="close-gallery-btn-desktop"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              {/* Photo grid */}
              <div className="flex-1 overflow-y-auto px-6 pb-8" data-testid="gallery-desktop-grid">
                <div className="max-w-5xl mx-auto grid grid-cols-2 lg:grid-cols-3 gap-3">
                  {stall.gallery.map((img, i) => (
                    <div
                      key={i}
                      className="aspect-[4/3] rounded-lg overflow-hidden cursor-pointer group"
                      onClick={() => { setPhotoIndex(i); setSinglePhotoView(true); }}
                      data-testid={`gallery-grid-photo-${i}`}
                    >
                      <img
                        src={getImageUrl(img)}
                        alt={`Gallery ${i + 1}`}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Single photo view header */}
              <div className="flex items-center justify-between px-6 py-4" style={{ zIndex: 100000 }}>
                <button
                  onClick={() => setSinglePhotoView(false)}
                  className="flex items-center gap-2 text-white hover:text-[#FFD12D] transition-colors font-semibold text-sm"
                  data-testid="gallery-back-to-grid"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Back to gallery
                </button>
                <span className="text-white font-semibold text-base">{photoIndex + 1} / {stall.gallery.length}</span>
                <button
                  onClick={() => { setShowAllPhotos(false); setPhotoIndex(0); setSinglePhotoView(false); }}
                  className="bg-[#FFD12D] hover:bg-[#e6bc28] text-[#252525] rounded-full w-10 h-10 flex items-center justify-center transition-colors"
                  data-testid="close-gallery-single-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Single photo */}
              <div className="flex-1 flex items-center justify-center w-full px-20 lg:px-32">
                <img
                  src={getImageUrl(stall.gallery[photoIndex])}
                  alt={`Gallery ${photoIndex + 1}`}
                  className="max-w-full max-h-[80vh] object-contain rounded-lg"
                />
              </div>

              {/* Left arrow */}
              {stall.gallery.length > 1 && (
                <button
                  onClick={() => setPhotoIndex((photoIndex - 1 + stall.gallery.length) % stall.gallery.length)}
                  className="absolute left-6 top-1/2 -translate-y-1/2 bg-[#FFD12D] hover:bg-[#e6bc28] text-[#252525] rounded-full w-12 h-12 flex items-center justify-center transition-colors shadow-lg"
                  style={{ zIndex: 100000 }}
                  data-testid="gallery-prev-btn"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
              )}

              {/* Right arrow */}
              {stall.gallery.length > 1 && (
                <button
                  onClick={() => setPhotoIndex((photoIndex + 1) % stall.gallery.length)}
                  className="absolute right-6 top-1/2 -translate-y-1/2 bg-[#FFD12D] hover:bg-[#e6bc28] text-[#252525] rounded-full w-12 h-12 flex items-center justify-center transition-colors shadow-lg"
                  style={{ zIndex: 100000 }}
                  data-testid="gallery-next-btn"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              )}
            </>
          )}
        </div>
      </div>,
      document.body
    )}
    </>
  );
};

export default StallModal;
